import { useCallback, useEffect, useMemo, useState } from 'react'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import type { ChatSearchHit } from 'tripPlanner/api/types'
import { useChatSearch } from 'tripPlanner/hooks/api/chat'
import { MIN_CHAT_SEARCH_QUERY_LENGTH } from './tripPlannerNavigationConstants'

const SEARCH_DEBOUNCE_MS = 250
const SEARCH_STALE_TIME_MS = 30_000
const EMPTY_RESULTS: Array<ChatSearchHit> = []

export type ChatSearchStatus = 'pending' | 'results' | 'error' | 'empty'

function getChatSearchStatus(hasResults: boolean, isPending: boolean, isError: boolean): ChatSearchStatus {
  if (hasResults) return 'results'
  if (isPending) return 'pending'
  if (isError) return 'error'
  return 'empty'
}

/** @param query Already trimmed. */
export default function useChatSearchResults(query: string) {
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const updateDebouncedQuery = useDebouncedCallback(setDebouncedQuery, SEARCH_DEBOUNCE_MS, [])

  useEffect(() => {
    updateDebouncedQuery(query)
    return updateDebouncedQuery.cancel
  }, [query, updateDebouncedQuery])

  const chatSearch = useChatSearch(debouncedQuery, {
    enabled: debouncedQuery.length >= MIN_CHAT_SEARCH_QUERY_LENGTH,
    staleTime: SEARCH_STALE_TIME_MS,
  })
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = chatSearch
  const [loadMoreError, setLoadMoreError] = useState<unknown>()

  const isLoadMoreError = chatSearch.isError && chatSearch.error === loadMoreError
  const isWaitingForDebounce = debouncedQuery !== query
  const hasCurrentResults = !isWaitingForDebounce && !chatSearch.isPreviousData
  const firstPage = hasCurrentResults ? chatSearch.data?.pages[0] : undefined

  const results = useMemo(
    () => (hasCurrentResults ? chatSearch.data?.pages.flatMap(page => page.result) : undefined) ?? EMPTY_RESULTS,
    [chatSearch.data, hasCurrentResults],
  )

  const loadMore = useCallback(async() => {
    if (!hasNextPage || isFetchingNextPage) return
    const nextPage = await fetchNextPage()
    setLoadMoreError(nextPage.error)
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return {
    status: getChatSearchStatus(
      results.length > 0,
      isWaitingForDebounce || chatSearch.isLoading || chatSearch.isFetching,
      chatSearch.isError,
    ),
    results,
    /** The API returns no total, so a count can only describe the first page. */
    firstPageCount: firstPage?.result.length ?? 0,
    hasMoreThanFirstPage: !!firstPage?.hasMore,
    isFetching: chatSearch.isFetching,
    isLoadMoreError,
    hasMore: hasCurrentResults && !!hasNextPage,
    isLoadingMore: isFetchingNextPage,
    loadMore,
  }
}
